/**
 * Időpontfoglaló — Admin oldal
 * Tabs: Alapadatok | Szolgáltatások | Munkatársak | Nyitvatartás | Foglalások
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { get, post, patch, del } from '../../lib/api';
import {
  PageHeader, Button, Badge, Card, Input, Modal, EmptyState,
  LoadingOverlay, Select, Textarea, Tabs,
} from '../../components/ui';
import { useToast } from '../../components/ui/Toast';

// ── Típusok ───────────────────────────────────────────────────────────────────

interface Business {
  id: string; slug: string; name: string; category: string;
  email: string; phone: string; address: string; city: string;
  description: string; status: string; booking_enabled: boolean;
  advance_booking_days: number; created_at: string;
  subdomain_provider: 'modulum' | 'foglalopont';
}

interface Service {
  id: string; name: string; description: string;
  duration_min: number; price_net_huf: number; vat_rate: string;
  buffer_before: number; buffer_after: number; color: string;
  is_active: boolean; max_parallel: number; sort_order: number;
  staff_ids: string[];
}

interface Staff {
  id: string; user_id: string | null; display_name: string;
  bio: string; color: string; is_active: boolean; sort_order: number;
  email?: string; user_email?: string; photo_url?: string | null;
  is_owner?: boolean;
}

interface HourRow {
  day_of_week: number; is_open: boolean; open_time: string; close_time: string;
}

interface Settings {
  advance_booking_days: number; min_advance_hours: number;
  booking_enabled: boolean; cancel_allowed: boolean;
  cancel_deadline_hours: number; slot_step_min: number;
  max_bookings_per_day: number | null; auto_confirm: boolean;
  custom_message: string; allow_staff_select: boolean; require_phone: boolean;
}

interface Booking {
  id: string; status: string; start_at: string; end_at: string;
  guest_name: string; guest_email: string; guest_phone: string;
  notes: string; source: string; cancel_reason: string | null;
  staff_display_name?: string; services?: { name: string; duration_min: number }[];
}

const DAYS = ['Vasárnap', 'Hétfő', 'Kedd', 'Szerda', 'Csütörtök', 'Péntek', 'Szombat'];
const DAYS_SHORT = ['V', 'H', 'K', 'Sze', 'Cs', 'P', 'Szo'];

const STATUS_BADGE: Record<string, 'default' | 'info' | 'success' | 'error' | 'warning'> = {
  pending: 'warning', confirmed: 'info', completed: 'success',
  cancelled: 'error', no_show: 'default',
};
const STATUS_LABEL: Record<string, string> = {
  pending: 'Függőben', confirmed: 'Visszaigazolt', completed: 'Teljesített',
  cancelled: 'Lemondva', no_show: 'Nem jelent meg',
};

const CATEGORY_OPTIONS = [
  { value: 'beauty', label: 'Szépségszalon' },
  { value: 'health', label: 'Egészségügy' },
  { value: 'fitness', label: 'Fitnesz / Sport' },
  { value: 'consulting', label: 'Tanácsadás' },
  { value: 'education', label: 'Oktatás' },
  { value: 'other', label: 'Egyéb' },
];

function fmtDate(iso: string) {
  try { return new Date(iso).toLocaleDateString('hu-HU', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return '—'; }
}
function fmtTime(iso: string) {
  try { return new Date(iso).toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' }); }
  catch { return '—'; }
}
function fmtMoney(n: number) { return Math.round(n).toLocaleString('hu-HU') + ' Ft'; }

// ── Segédkomponensek ──────────────────────────────────────────────────────────

function ColorDot({ color }: { color: string }) {
  return <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', background: color, marginRight: 6 }} />;
}

// ── Alapadatok tab ────────────────────────────────────────────────────────────

function BasicInfoTab({ biz, onSaved }: { biz: Business; onSaved: (b: Business) => void }) {
  const { toast } = useToast();
  const [form, setForm] = useState({ ...biz });
  const [saving, setSaving] = useState(false);

  function set(k: string, v: any) { setForm(f => ({ ...f, [k]: v })); }

  async function save() {
    setSaving(true);
    try {
      const updated = await patch<Business>(`/booking/${biz.id}`, {
        slug: form.slug, name: form.name, category: form.category,
        email: form.email, phone: form.phone, address: form.address,
        city: form.city, description: form.description, status: form.status,
        subdomain_provider: form.subdomain_provider,
      });
      onSaved(updated);
      toast('Profil mentve.', 'success');
    } catch (e: any) { toast(e.message || 'Hiba történt.', 'error'); }
    setSaving(false);
  }

  const domain = form.subdomain_provider === 'foglalopont' ? 'foglalopont.hu' : 'modulum.hu';
  const publicUrl = `https://${form.slug}.${domain}`;

  return (
    <div style={{ maxWidth: 640 }}>
      <LoadingOverlay visible={saving} />
      <Card style={{ marginBottom: 16, padding: 16 }}>
        <p style={{ fontSize: 13, color: 'var(--color-muted)' }}>
          Nyilvános foglalási link:&nbsp;
          <a href={publicUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--primary)' }}>{publicUrl}</a>
        </p>
      </Card>

      <div className="grid grid-cols-2 gap-4">
        <div style={{ gridColumn: '1 / -1' }}>
          <Input label="Üzlet neve *" value={form.name} onChange={e => set('name', e.target.value)} />
        </div>
        <Input label="Slug (URL)" value={form.slug} onChange={e => set('slug', e.target.value)}
          helperText="Csak kisbetű, szám, kötőjel" />
        <Select label="Kategória" value={form.category} options={CATEGORY_OPTIONS}
          onChange={e => set('category', e.target.value)} />
        <Input label="E-mail" type="email" value={form.email} onChange={e => set('email', e.target.value)} />
        <Input label="Telefonszám" value={form.phone} onChange={e => set('phone', e.target.value)} />
        <Input label="Cím" value={form.address} onChange={e => set('address', e.target.value)} />
        <Input label="Város" value={form.city} onChange={e => set('city', e.target.value)} />
        <div style={{ gridColumn: '1 / -1' }}>
          <Textarea label="Leírás" rows={4} value={form.description}
            onChange={e => set('description', e.target.value)} />
        </div>
        <Select label="Státusz" value={form.status}
          options={[
            { value: 'active', label: 'Aktív' },
            { value: 'paused', label: 'Szünetel' },
            { value: 'inactive', label: 'Inaktív' },
          ]}
          onChange={e => set('status', e.target.value)} />
        <Select label="Domain" value={form.subdomain_provider ?? 'modulum'}
          options={[
            { value: 'modulum', label: 'slug.modulum.hu' },
            { value: 'foglalopont', label: 'slug.foglalopont.hu' },
          ]}
          onChange={e => set('subdomain_provider', e.target.value)}
          helperText="Az aldomaint a foglalási profil slug-ja egészíti ki" />
      </div>

      <div style={{ marginTop: 20 }}>
        <Button onClick={save} disabled={saving}>Mentés</Button>
      </div>
    </div>
  );
}

// ── Beállítások tab ───────────────────────────────────────────────────────────

function SettingsTab({ bizId }: { bizId: string }) {
  const { toast } = useToast();
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    get<Settings>(`/booking/${bizId}/settings`).then(s => { setSettings(s); setLoading(false); }).catch(() => setLoading(false));
  }, [bizId]);

  function set(k: string, v: any) { setSettings(s => s ? { ...s, [k]: v } : null); }

  async function save() {
    if (!settings) return;
    setSaving(true);
    try {
      const updated = await patch<Settings>(`/booking/${bizId}/settings`, settings);
      setSettings(updated);
      toast('Beállítások mentve.', 'success');
    } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
    setSaving(false);
  }

  if (loading || !settings) return <div style={{ padding: 40, textAlign: 'center' }}>Betöltés...</div>;

  return (
    <div style={{ maxWidth: 520 }}>
      <LoadingOverlay visible={saving} />
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="form-label">Foglalás engedélyezve</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
            <input type="checkbox" checked={settings.booking_enabled}
              onChange={e => set('booking_enabled', e.target.checked)} style={{ width: 18, height: 18 }} />
            <span style={{ fontSize: 14 }}>{settings.booking_enabled ? 'Igen' : 'Nem'}</span>
          </div>
        </div>
        <div>
          <label className="form-label">Lemondás engedélyezett</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
            <input type="checkbox" checked={settings.cancel_allowed}
              onChange={e => set('cancel_allowed', e.target.checked)} style={{ width: 18, height: 18 }} />
            <span style={{ fontSize: 14 }}>{settings.cancel_allowed ? 'Igen' : 'Nem'}</span>
          </div>
        </div>
        <div>
          <label className="form-label">Automatikus visszaigazolás</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
            <input type="checkbox" checked={settings.auto_confirm ?? false}
              onChange={e => set('auto_confirm', e.target.checked)} style={{ width: 18, height: 18 }} />
            <span style={{ fontSize: 14 }}>{settings.auto_confirm ? 'Be (azonnal visszaigazol)' : 'Ki (manuális visszaigazolás)'}</span>
          </div>
        </div>
        <div>
          <label className="form-label">Munkatárs-választás</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
            <input type="checkbox" checked={settings.allow_staff_select ?? true}
              onChange={e => set('allow_staff_select', e.target.checked)} style={{ width: 18, height: 18 }} />
            <span style={{ fontSize: 14 }}>{settings.allow_staff_select ?? true ? 'Ügyfél választhat munkatársat' : 'Automatikus hozzárendelés'}</span>
          </div>
        </div>
        <div>
          <label className="form-label">Telefonszám kötelező</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
            <input type="checkbox" checked={settings.require_phone ?? false}
              onChange={e => set('require_phone', e.target.checked)} style={{ width: 18, height: 18 }} />
            <span style={{ fontSize: 14 }}>{settings.require_phone ? 'Igen' : 'Nem (opcionális)'}</span>
          </div>
        </div>
        <Input label="Előre foglalható (nap)" type="number" min={1} max={365}
          value={settings.advance_booking_days || ''}
          onChange={e => set('advance_booking_days', e.target.value === '' ? 0 : parseInt(e.target.value))} />
        <Input label="Minimum előre (óra)" type="number" min={0} max={168}
          value={settings.min_advance_hours === 0 ? '' : settings.min_advance_hours}
          onChange={e => set('min_advance_hours', e.target.value === '' ? 0 : parseInt(e.target.value))} />
        <Input label="Lemondás határideje (óra)" type="number" min={0}
          value={settings.cancel_deadline_hours === 0 ? '' : settings.cancel_deadline_hours}
          onChange={e => set('cancel_deadline_hours', e.target.value === '' ? 0 : parseInt(e.target.value))} />
        <Input label="Időpont lépésköz (perc)" type="number" min={5} max={120}
          value={settings.slot_step_min || ''}
          onChange={e => set('slot_step_min', e.target.value === '' ? 0 : parseInt(e.target.value))} />
        <Input label="Max foglalás/nap (üres = korlátlan)" type="number" min={0}
          value={settings.max_bookings_per_day == null ? '' : String(settings.max_bookings_per_day)}
          onChange={e => set('max_bookings_per_day', e.target.value === '' ? null : parseInt(e.target.value))} />
        <div style={{ gridColumn: '1 / -1' }}>
          <Textarea label="Üzenet a foglalónak" rows={3} value={settings.custom_message || ''}
            onChange={e => set('custom_message', e.target.value)} />
        </div>
      </div>
      <div style={{ gridColumn: '1 / -1', marginTop: 4 }}>
        <label className="form-label">Munkatársak naptárláthatósága</label>
        <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
          {[{ v: 'own', label: 'Csak saját foglalások' }, { v: 'all', label: 'Mindenki foglalásai' }].map(opt => (
            <button key={opt.v} type="button"
              onClick={async () => {
                try { await patch(`/booking/${bizId}/staff-visibility`, { staff_visibility: opt.v }); toast('Láthatóság mentve.', 'success'); }
                catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
              }}
              style={{ padding: '8px 16px', borderRadius: 8, border: '1.5px solid var(--color-border)', fontSize: 13, cursor: 'pointer', fontWeight: 500, background: 'transparent' }}>
              {opt.label}
            </button>
          ))}
        </div>
        <div style={{ fontSize: 12, color: 'var(--color-muted)', marginTop: 4 }}>
          Meghatározza, hogy a munkatársak a naptar.modulum.hu-n látják-e egymás foglalásait.
        </div>
      </div>
      <div style={{ marginTop: 20 }}>
        <Button onClick={save} disabled={saving}>Mentés</Button>
      </div>
    </div>
  );
}

// ── Szolgáltatások tab ────────────────────────────────────────────────────────

const EMPTY_SVC: Omit<Service, 'id'> = {
  name: '', description: '', duration_min: 60, price_net_huf: 0,
  vat_rate: '27', buffer_before: 0, buffer_after: 0, color: '#6366f1',
  is_active: true, max_parallel: 1, sort_order: 0, staff_ids: [],
};

function ServicesTab({ bizId, staff }: { bizId: string; staff: Staff[] }) {
  const { toast } = useToast();
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Service | null>(null);
  const [form, setForm] = useState<Omit<Service, 'id'>>(EMPTY_SVC);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    get<Service[]>(`/booking/${bizId}/services`).then(r => { setServices(r); setLoading(false); });
  }, [bizId]);

  useEffect(() => { load(); }, [load]);

  function openNew() { setEditing(null); setForm(EMPTY_SVC); setShowModal(true); }
  function openEdit(s: Service) {
    setEditing(s);
    setForm({ name: s.name, description: s.description, duration_min: s.duration_min,
      price_net_huf: s.price_net_huf, vat_rate: s.vat_rate, buffer_before: s.buffer_before,
      buffer_after: s.buffer_after, color: s.color, is_active: s.is_active,
      max_parallel: s.max_parallel, sort_order: s.sort_order, staff_ids: s.staff_ids || [],
    });
    setShowModal(true);
  }

  function set(k: string, v: any) { setForm(f => ({ ...f, [k]: v })); }
  function toggleStaff(id: string) {
    setForm(f => ({
      ...f, staff_ids: f.staff_ids.includes(id) ? f.staff_ids.filter(x => x !== id) : [...f.staff_ids, id],
    }));
  }

  async function save() {
    setSaving(true);
    try {
      if (editing) {
        const updated = await patch<Service>(`/booking/${bizId}/services/${editing.id}`, form);
        setServices(ss => ss.map(s => s.id === editing.id ? updated : s));
      } else {
        const created = await post<Service>(`/booking/${bizId}/services`, form);
        setServices(ss => [...ss, created]);
      }
      setShowModal(false);
      toast('Szolgáltatás mentve.', 'success');
    } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
    setSaving(false);
  }

  async function remove(id: string) {
    if (!confirm('Biztosan törlöd ezt a szolgáltatást?')) return;
    try {
      await del(`/booking/${bizId}/services/${id}`);
      setServices(ss => ss.filter(s => s.id !== id));
      toast('Törölve.', 'success');
    } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Betöltés...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Button size="sm" onClick={openNew}>+ Új szolgáltatás</Button>
      </div>
      {services.length === 0
        ? <EmptyState title="Nincsenek szolgáltatások" description="Adj hozzá az első szolgáltatást." />
        : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {services.map(s => (
              <Card key={s.id} style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <ColorDot color={s.color} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--color-muted)' }}>
                    {s.duration_min} perc · {fmtMoney(s.price_net_huf)} + ÁFA
                    {(s.buffer_before || s.buffer_after) ? ` · szünet: ${s.buffer_before}/${s.buffer_after} p` : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <Badge variant={s.is_active ? 'success' : 'default'}>{s.is_active ? 'Aktív' : 'Inaktív'}</Badge>
                  <Button size="sm" variant="ghost" onClick={() => openEdit(s)}>Szerkeszt</Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(s.id)} style={{ color: 'var(--red)' }}>Töröl</Button>
                </div>
              </Card>
            ))}
          </div>
        )
      }

      <Modal isOpen={showModal} onClose={() => setShowModal(false)}
        title={editing ? 'Szolgáltatás szerkesztése' : 'Új szolgáltatás'}
        footer={<><Button onClick={save} disabled={saving}>Mentés</Button><Button variant="ghost" onClick={() => setShowModal(false)}>Mégsem</Button></>}
      >
        <LoadingOverlay visible={saving} />
        <div className="grid grid-cols-2 gap-4">
          <div style={{ gridColumn: '1 / -1' }}>
            <Input label="Megnevezés *" value={form.name} onChange={e => set('name', e.target.value)} />
          </div>
          <Input label="Időtartam (perc) *" type="number" min={1} value={form.duration_min || ''}
            onChange={e => set('duration_min', e.target.value === '' ? 0 : parseInt(e.target.value))} />
          <Input label="Nettó ár (Ft)" type="number" min={0} value={form.price_net_huf === 0 ? '' : form.price_net_huf}
            onChange={e => set('price_net_huf', e.target.value === '' ? 0 : parseInt(e.target.value))} />
          <Select label="ÁFA kulcs" value={form.vat_rate}
            options={[{ value: '0', label: '0%' }, { value: '5', label: '5%' }, { value: '18', label: '18%' }, { value: '27', label: '27%' }]}
            onChange={e => set('vat_rate', e.target.value)} />
          <Input label="Szünet előtte (perc)" type="number" min={0} value={form.buffer_before === 0 ? '' : form.buffer_before}
            onChange={e => set('buffer_before', e.target.value === '' ? 0 : parseInt(e.target.value))} />
          <Input label="Szünet utána (perc)" type="number" min={0} value={form.buffer_after === 0 ? '' : form.buffer_after}
            onChange={e => set('buffer_after', e.target.value === '' ? 0 : parseInt(e.target.value))} />
          <Input label="Max párhuzamos foglalás" type="number" min={1} value={form.max_parallel || ''}
            onChange={e => set('max_parallel', e.target.value === '' ? 0 : parseInt(e.target.value))} />
          <div>
            <label className="form-label">Szín</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
              <input type="color" value={form.color} onChange={e => set('color', e.target.value)}
                style={{ width: 40, height: 36, padding: 2, border: '1px solid var(--color-border)', borderRadius: 6 }} />
              <span style={{ fontSize: 13, color: 'var(--color-muted)' }}>{form.color}</span>
            </div>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label className="form-label">Aktív</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
              <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)}
                style={{ width: 18, height: 18 }} />
              <span style={{ fontSize: 14 }}>{form.is_active ? 'Igen' : 'Nem'}</span>
            </div>
          </div>
          {staff.length > 0 && (
            <div style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Elvégezheti (munkatársak)</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
                {staff.map(st => (
                  <label key={st.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                    <input type="checkbox" checked={form.staff_ids.includes(st.id)}
                      onChange={() => toggleStaff(st.id)} />
                    <ColorDot color={st.color || '#888'} />
                    {st.display_name}
                  </label>
                ))}
              </div>
            </div>
          )}
          <div style={{ gridColumn: '1 / -1' }}>
            <Textarea label="Leírás" rows={3} value={form.description}
              onChange={e => set('description', e.target.value)} />
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ── Munkatársak tab ───────────────────────────────────────────────────────────

const EMPTY_STAFF = { display_name: '', bio: '', color: '#6366f1', is_active: true, sort_order: 0, email: '' };

// ── Munkatárs nyitvatartás modal ───────────────────────────────────────────────

function StaffHoursModal({ bizId, staff, onClose }: { bizId: string; staff: Staff; onClose: () => void }) {
  const { toast } = useToast();
  const [hours, setHours] = useState<HourRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    get<HourRow[]>(`/booking/${bizId}/staff/${staff.id}/hours`).then(r => {
      const sorted = [...r].sort((a, b) => a.day_of_week - b.day_of_week);
      if (sorted.length === 0) {
        setHours(Array.from({ length: 7 }, (_, i) => ({
          day_of_week: i, is_open: i >= 1 && i <= 5, open_time: '09:00', close_time: '18:00',
        })));
      } else {
        const full = Array.from({ length: 7 }, (_, i) => {
          const found = sorted.find(h => h.day_of_week === i);
          return found || { day_of_week: i, is_open: false, open_time: '09:00', close_time: '18:00' };
        });
        setHours(full);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [bizId, staff.id]);

  function update(dow: number, k: string, v: any) {
    setHours(rows => rows.map(r => r.day_of_week === dow ? { ...r, [k]: v } : r));
  }

  async function save() {
    setSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`/api/booking/${bizId}/staff/${staff.id}/hours`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(hours),
      });
      if (!res.ok) throw new Error('Mentési hiba');
      toast('Nyitvatartás mentve.', 'success');
      onClose();
    } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
    setSaving(false);
  }

  return (
    <Modal isOpen onClose={onClose} title={`${staff.display_name} — Egyéni nyitvatartás`}
      footer={<><Button onClick={save} disabled={saving || loading}>Mentés</Button><Button variant="ghost" onClick={onClose}>Mégsem</Button></>}
    >
      <LoadingOverlay visible={saving} />
      {loading ? <div style={{ padding: 24, textAlign: 'center' }}>Betöltés...</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '100px 60px 110px 16px 110px', gap: 8, padding: '4px 0', fontSize: 12, fontWeight: 600, color: 'var(--color-muted)' }}>
            <span>Nap</span><span>Nyitva</span><span>Nyitás</span><span></span><span>Zárás</span>
          </div>
          {hours.map(row => (
            <div key={row.day_of_week} style={{ display: 'grid', gridTemplateColumns: '100px 60px 110px 16px 110px', gap: 8, alignItems: 'center', padding: '6px 8px', background: row.is_open ? 'var(--color-surface)' : '#f9f9f9', borderRadius: 6, border: '1px solid var(--color-border)' }}>
              <span style={{ fontSize: 14, fontWeight: row.is_open ? 600 : 400 }}>{DAYS[row.day_of_week]}</span>
              <input type="checkbox" checked={row.is_open} onChange={e => update(row.day_of_week, 'is_open', e.target.checked)} style={{ width: 18, height: 18 }} />
              <input type="time" value={row.open_time} disabled={!row.is_open} onChange={e => update(row.day_of_week, 'open_time', e.target.value)} style={{ padding: '6px 8px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 14, opacity: row.is_open ? 1 : 0.4 }} />
              <span style={{ textAlign: 'center', color: 'var(--color-muted)', fontSize: 12 }}>–</span>
              <input type="time" value={row.close_time} disabled={!row.is_open} onChange={e => update(row.day_of_week, 'close_time', e.target.value)} style={{ padding: '6px 8px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 14, opacity: row.is_open ? 1 : 0.4 }} />
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

// ── Naptárláthatóság modal ────────────────────────────────────────────────────

function CalendarAccessModal({ bizId, staff, allStaff, onClose }: { bizId: string; staff: Staff; allStaff: Staff[]; onClose: () => void }) {
  const { toast } = useToast();
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const others = allStaff.filter(s => s.id !== staff.id);

  useEffect(() => {
    get<string[]>(`/booking/${bizId}/staff/${staff.id}/calendar-access`)
      .then(ids => { setSelected(ids); setLoading(false); })
      .catch(() => setLoading(false));
  }, [bizId, staff.id]);

  function toggle(id: string) {
    setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  }

  async function save() {
    setSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`/api/booking/${bizId}/staff/${staff.id}/calendar-access`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ staff_ids: selected }),
      });
      if (!res.ok) throw new Error('Mentési hiba');
      toast('Naptárláthatóság mentve.', 'success');
      onClose();
    } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
    setSaving(false);
  }

  return (
    <Modal isOpen onClose={onClose} title={`${staff.display_name} — Naptárláthatóság`}
      footer={<><Button onClick={save} disabled={saving || loading}>Mentés</Button><Button variant="ghost" onClick={onClose}>Mégsem</Button></>}
    >
      <LoadingOverlay visible={saving} />
      <div style={{ fontSize: 13, color: 'var(--color-muted)', marginBottom: 12 }}>
        Kiknek a naptárját látja <strong>{staff.display_name}</strong>?
      </div>
      {loading ? <div style={{ padding: 16, textAlign: 'center' }}>Betöltés...</div> : others.length === 0 ? (
        <div style={{ fontSize: 14, color: 'var(--color-muted)', padding: 16, textAlign: 'center' }}>Nincs más munkatárs.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {others.map(s => (
            <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--color-border)', background: selected.includes(s.id) ? 'var(--color-surface)' : 'transparent' }}>
              <input type="checkbox" checked={selected.includes(s.id)} onChange={() => toggle(s.id)} style={{ width: 18, height: 18 }} />
              {s.photo_url
                ? <img src={s.photo_url} alt={s.display_name} style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }} />
                : <div style={{ width: 28, height: 28, borderRadius: '50%', background: s.color || '#888', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 13 }}>{s.display_name[0]?.toUpperCase()}</div>
              }
              <span style={{ fontSize: 14, fontWeight: 500 }}>{s.display_name}</span>
              {s.is_owner && <Badge variant="info">Tulajdonos</Badge>}
            </label>
          ))}
        </div>
      )}
    </Modal>
  );
}

// ── Munkatársak tab ───────────────────────────────────────────────────────────

function StaffTab({ bizId, onStaffChange }: { bizId: string; onStaffChange: (s: Staff[]) => void }) {
  const { toast } = useToast();
  const [staff, setStaff] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Staff | null>(null);
  const [form, setForm] = useState(EMPTY_STAFF);
  const [saving, setSaving] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [hoursFor, setHoursFor] = useState<Staff | null>(null);
  const [calAccessFor, setCalAccessFor] = useState<Staff | null>(null);

  async function uploadPhoto(staffId: string, file: File) {
    setPhotoUploading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const fd = new FormData();
      fd.append('photo', file);
      const res = await fetch(`/api/booking/${bizId}/staff/${staffId}/photo`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Hiba.'); }
      const { photo_url } = await res.json();
      setStaff(ss => { const next = ss.map(s => s.id === staffId ? { ...s, photo_url } : s); onStaffChange(next); return next; });
      toast('Fotó feltöltve.', 'success');
    } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
    setPhotoUploading(false);
  }

  async function deletePhoto(staffId: string) {
    try {
      await del(`/booking/${bizId}/staff/${staffId}/photo`);
      setStaff(ss => { const next = ss.map(s => s.id === staffId ? { ...s, photo_url: null } : s); onStaffChange(next); return next; });
      toast('Fotó törölve.', 'success');
    } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
  }

  const load = useCallback(() => {
    get<Staff[]>(`/booking/${bizId}/staff`)
      .then(r => { setStaff(r); onStaffChange(r); setLoading(false); })
      .catch(() => setLoading(false));
  }, [bizId, onStaffChange]);

  useEffect(() => { load(); }, [load]);

  function openNew() { setEditing(null); setForm(EMPTY_STAFF); setShowModal(true); }
  function openEdit(s: Staff) {
    setEditing(s);
    setForm({ display_name: s.display_name, bio: s.bio || '', color: s.color || '#6366f1',
      is_active: s.is_active, sort_order: s.sort_order, email: s.email || '' });
    setShowModal(true);
  }

  function setF(k: string, v: any) { setForm(f => ({ ...f, [k]: v })); }

  async function save() {
    setSaving(true);
    try {
      const updated = await post<Staff>(`/booking/${bizId}/staff`, {
        ...form, ...(editing ? { id: editing.id } : {}),
      });
      setStaff(ss => {
        const next = editing ? ss.map(s => s.id === editing.id ? updated : s) : [...ss, updated];
        onStaffChange(next);
        return next;
      });
      setShowModal(false);
      toast('Munkatárs mentve.', 'success');
    } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
    setSaving(false);
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Betöltés...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Button size="sm" onClick={openNew}>+ Új munkatárs</Button>
      </div>
      {staff.length === 0
        ? <EmptyState title="Nincsenek munkatársak" description="Add hozzá az első munkatársat." />
        : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {staff.map(s => (
              <Card key={s.id} style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', ...(s.is_owner ? { borderColor: 'var(--color-primary)', borderWidth: 2 } : {}) }}>
                {s.photo_url
                  ? <img src={s.photo_url} alt={s.display_name} style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${s.color || '#888'}` }} />
                  : <div style={{ width: 44, height: 44, borderRadius: '50%', background: s.color || '#888', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 18, flexShrink: 0 }}>
                      {s.display_name[0]?.toUpperCase()}
                    </div>
                }
                <div style={{ flex: 1, minWidth: 120 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontWeight: 700, fontSize: 14 }}>{s.display_name}</span>
                    {s.is_owner && <Badge variant="info">Én</Badge>}
                  </div>
                  {s.email && <div style={{ fontSize: 12, color: 'var(--color-muted)' }}>{s.email}</div>}
                </div>
                <Badge variant={s.is_active ? 'success' : 'default'}>{s.is_active ? 'Aktív' : 'Inaktív'}</Badge>
                <label style={{ cursor: 'pointer', display: 'inline-flex' }}>
                  <input type="file" accept="image/*" style={{ display: 'none' }} disabled={photoUploading}
                    onChange={e => { const f = e.target.files?.[0]; if (f) uploadPhoto(s.id, f); e.target.value = ''; }} />
                  <span style={{ fontSize: 12, padding: '4px 8px', borderRadius: 6, border: '1px solid var(--color-border)', cursor: 'pointer', background: 'transparent' }}>📷 Fotó</span>
                </label>
                {s.photo_url && (
                  <Button size="sm" variant="ghost" onClick={() => deletePhoto(s.id)}>🗑️</Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => setHoursFor(s)} title="Egyéni nyitvatartás">🕐</Button>
                {staff.length > 1 && (
                  <Button size="sm" variant="ghost" onClick={() => setCalAccessFor(s)} title="Naptárláthatóság">👁</Button>
                )}
                {s.email && (
                  <Button size="sm" variant="ghost" onClick={async () => {
                    try {
                      await post(`/booking/${bizId}/staff/${s.id}/invite`, {});
                      toast('Meghívó elküldve!', 'success');
                    } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
                  }}>📧</Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => openEdit(s)}>Szerkeszt</Button>
              </Card>
            ))}
          </div>
        )
      }

      <Modal isOpen={showModal} onClose={() => setShowModal(false)}
        title={editing ? (editing.is_owner ? 'Saját adataim' : 'Munkatárs szerkesztése') : 'Új munkatárs'}
        footer={<><Button onClick={save} disabled={saving}>Mentés</Button><Button variant="ghost" onClick={() => setShowModal(false)}>Mégsem</Button></>}
      >
        <LoadingOverlay visible={saving} />
        <div className="grid grid-cols-2 gap-4">
          <div style={{ gridColumn: '1 / -1' }}>
            <Input label="Megjelenítendő név *" value={form.display_name}
              onChange={e => setF('display_name', e.target.value)} />
          </div>
          <div>
            <label className="form-label">Szín</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
              <input type="color" value={form.color} onChange={e => setF('color', e.target.value)}
                style={{ width: 40, height: 36, padding: 2, border: '1px solid var(--color-border)', borderRadius: 6 }} />
            </div>
          </div>
          <div>
            <label className="form-label">Aktív</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
              <input type="checkbox" checked={form.is_active} onChange={e => setF('is_active', e.target.checked)}
                style={{ width: 18, height: 18 }} />
              <span style={{ fontSize: 14 }}>{form.is_active ? 'Igen' : 'Nem'}</span>
            </div>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <Input label="Email (naptar.modulum.hu bejelentkezéshez)" type="email"
              value={form.email} placeholder="pl. anna@fodrasz.hu"
              onChange={e => setF('email', e.target.value)} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <Textarea label="Bemutatkozás" rows={2} value={form.bio}
              onChange={e => setF('bio', e.target.value)} />
          </div>
        </div>
      </Modal>

      {hoursFor && (
        <StaffHoursModal bizId={bizId} staff={hoursFor} onClose={() => { setHoursFor(null); }} />
      )}
      {calAccessFor && (
        <CalendarAccessModal bizId={bizId} staff={calAccessFor} allStaff={staff} onClose={() => setCalAccessFor(null)} />
      )}
    </div>
  );
}

// ── Nyitvatartás tab ──────────────────────────────────────────────────────────

function HoursTab({ bizId }: { bizId: string }) {
  const { toast } = useToast();
  const [hours, setHours] = useState<HourRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    get<HourRow[]>(`/booking/${bizId}/hours`).then(r => {
      const sorted = [...r].sort((a, b) => a.day_of_week - b.day_of_week);
      if (sorted.length === 0) {
        // Alapértelmezett 7 sor
        setHours(Array.from({ length: 7 }, (_, i) => ({
          day_of_week: i, is_open: i >= 1 && i <= 5, open_time: '09:00', close_time: '18:00',
        })));
      } else {
        setHours(sorted);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [bizId]);

  function update(dow: number, k: string, v: any) {
    setHours(rows => rows.map(r => r.day_of_week === dow ? { ...r, [k]: v } : r));
  }

  async function save() {
    setSaving(true);
    try {
      const updated = await (patch as any)(`/booking/${bizId}/hours`, hours);
      setHours(updated);
      toast('Nyitvatartás mentve.', 'success');
    } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
    setSaving(false);
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Betöltés...</div>;

  return (
    <div style={{ maxWidth: 520 }}>
      <LoadingOverlay visible={saving} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {/* Header */}
        <div style={{ display: 'grid', gridTemplateColumns: '100px 60px 110px 16px 110px', gap: 8, alignItems: 'center', padding: '4px 0', fontSize: 12, fontWeight: 600, color: 'var(--color-muted)' }}>
          <span>Nap</span><span>Nyitva</span><span>Nyitás</span><span></span><span>Zárás</span>
        </div>
        {hours.map(row => (
          <div key={row.day_of_week} style={{ display: 'grid', gridTemplateColumns: '100px 60px 110px 16px 110px', gap: 8, alignItems: 'center', padding: '6px 8px', background: row.is_open ? 'var(--color-surface)' : '#f9f9f9', borderRadius: 6, border: '1px solid var(--color-border)' }}>
            <span style={{ fontSize: 14, fontWeight: row.is_open ? 600 : 400 }}>
              {DAYS[row.day_of_week]}
            </span>
            <div>
              <input type="checkbox" checked={row.is_open}
                onChange={e => update(row.day_of_week, 'is_open', e.target.checked)}
                style={{ width: 18, height: 18 }} />
            </div>
            <input type="time" value={row.open_time} disabled={!row.is_open}
              onChange={e => update(row.day_of_week, 'open_time', e.target.value)}
              style={{ padding: '6px 8px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 14, opacity: row.is_open ? 1 : 0.4 }} />
            <span style={{ textAlign: 'center', color: 'var(--color-muted)', fontSize: 12 }}>–</span>
            <input type="time" value={row.close_time} disabled={!row.is_open}
              onChange={e => update(row.day_of_week, 'close_time', e.target.value)}
              style={{ padding: '6px 8px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 14, opacity: row.is_open ? 1 : 0.4 }} />
          </div>
        ))}
      </div>
      <div style={{ marginTop: 20 }}>
        <Button onClick={save} disabled={saving}>Mentés</Button>
      </div>
    </div>
  );
}

// ── Foglalások tab ────────────────────────────────────────────────────────────

function BookingsTab({ bizId }: { bizId: string }) {
  const { toast } = useToast();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; });
  const [dateTo, setDateTo] = useState(() => { const d = new Date(); d.setMonth(d.getMonth() + 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()}`; });
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Booking | null>(null);
  const [newStatus, setNewStatus] = useState('');
  const [reason, setReason] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ dateFrom, dateTo, page: String(page), pageSize: '25' });
      if (statusFilter) params.set('status', statusFilter);
      if (search) params.set('search', search);
      const data = await get<{ bookings: Booking[]; total: number }>(`/booking/${bizId}/bookings?${params}`);
      setBookings(data.bookings || []);
      setTotal(data.total || 0);
    } catch { /* */ }
    setLoading(false);
  }, [bizId, dateFrom, dateTo, statusFilter, search, page]);

  useEffect(() => { load(); }, [load]);

  async function updateStatus() {
    if (!selected || !newStatus) return;
    setUpdatingStatus(true);
    try {
      await patch(`/booking/${bizId}/bookings/${selected.id}/status`, { status: newStatus, reason });
      toast('Státusz frissítve.', 'success');
      setSelected(null);
      load();
    } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
    setUpdatingStatus(false);
  }

  const totalPages = Math.ceil(total / 25);

  return (
    <div>
      {/* Szűrők */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12, alignItems: 'flex-end' }}>
        <Input label="Dátumtól" type="date" value={dateFrom}
          onChange={e => { setDateFrom(e.target.value); setPage(1); }} style={{ width: 150 }} />
        <Input label="Dátumig" type="date" value={dateTo}
          onChange={e => { setDateTo(e.target.value); setPage(1); }} style={{ width: 150 }} />
        <Select label="Státusz" value={statusFilter} style={{ width: 160 }}
          options={[
            { value: '', label: 'Minden' },
            { value: 'pending', label: 'Függőben' },
            { value: 'confirmed', label: 'Visszaigazolt' },
            { value: 'completed', label: 'Teljesített' },
            { value: 'cancelled', label: 'Lemondva' },
            { value: 'no_show', label: 'Nem jelent meg' },
          ]}
          onChange={e => { setStatusFilter(e.target.value); setPage(1); }} />
        <Input label="Keresés" value={search} placeholder="Név, e-mail..."
          onChange={e => { setSearch(e.target.value); setPage(1); }} style={{ width: 200 }} />
      </div>

      {loading
        ? <div style={{ padding: 40, textAlign: 'center' }}>Betöltés...</div>
        : bookings.length === 0
          ? <EmptyState title="Nincsenek foglalások" description="A megadott szűrőkkel nem találtunk foglalást." />
          : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--color-border)' }}>
                    {['Dátum', 'Időpont', 'Vendég', 'Szolgáltatás', 'Munkatárs', 'Státusz', ''].map(h => (
                      <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--color-muted)', fontSize: 12 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bookings.map(b => (
                    <tr key={b.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '10px 10px' }}>{fmtDate(b.start_at)}</td>
                      <td style={{ padding: '10px 10px' }}>{fmtTime(b.start_at)} – {fmtTime(b.end_at)}</td>
                      <td style={{ padding: '10px 10px' }}>
                        <div style={{ fontWeight: 600 }}>{b.guest_name}</div>
                        <div style={{ fontSize: 11, color: 'var(--color-muted)' }}>{b.guest_email}</div>
                        {b.guest_phone && <div style={{ fontSize: 11, color: 'var(--color-muted)' }}>{b.guest_phone}</div>}
                      </td>
                      <td style={{ padding: '10px 10px' }}>
                        {b.services?.map((s, i) => <div key={i}>{s.name}</div>)}
                      </td>
                      <td style={{ padding: '10px 10px' }}>{b.staff_display_name || '—'}</td>
                      <td style={{ padding: '10px 10px' }}>
                        <Badge variant={STATUS_BADGE[b.status] || 'default'}>{STATUS_LABEL[b.status] || b.status}</Badge>
                      </td>
                      <td style={{ padding: '10px 10px' }}>
                        <Button size="sm" variant="ghost" onClick={() => { setSelected(b); setNewStatus(b.status); setReason(''); }}>
                          Módosít
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {totalPages > 1 && (
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16 }}>
                  <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Előző</Button>
                  <span style={{ lineHeight: '32px', fontSize: 13 }}>{page} / {totalPages}</span>
                  <Button size="sm" variant="ghost" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Következő →</Button>
                </div>
              )}
            </div>
          )
      }

      <Modal isOpen={!!selected} onClose={() => setSelected(null)} title="Foglalás státusza"
        footer={<><Button onClick={updateStatus} disabled={updatingStatus}>Mentés</Button><Button variant="ghost" onClick={() => setSelected(null)}>Mégsem</Button></>}
      >
        {selected && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 14 }}>
              <strong>{selected.guest_name}</strong> — {fmtDate(selected.start_at)} {fmtTime(selected.start_at)}
            </div>
            <Select label="Új státusz" value={newStatus}
              options={[
                { value: 'pending', label: 'Függőben' },
                { value: 'confirmed', label: 'Visszaigazolt' },
                { value: 'completed', label: 'Teljesített' },
                { value: 'cancelled', label: 'Lemondva' },
                { value: 'no_show', label: 'Nem jelent meg' },
              ]}
              onChange={e => setNewStatus(e.target.value)} />
            {(newStatus === 'cancelled' || newStatus === 'no_show') && (
              <Textarea label="Indoklás (opcionális)" rows={2} value={reason}
                onChange={e => setReason(e.target.value)} />
            )}
            {selected.notes && (
              <div style={{ padding: '8px 12px', background: 'var(--color-surface)', borderRadius: 6, fontSize: 13 }}>
                <strong>Megjegyzés:</strong> {selected.notes}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

// ── Naptár tab ────────────────────────────────────────────────────────────────

const CAL_STATUS_COLOR: Record<string, string> = {
  pending: '#f59e0b', confirmed: '#3b82f6', completed: '#10b981',
  cancelled: '#ef4444', no_show: '#9ca3af',
};

function getWeekStart(d: Date) {
  const day = d.getDay(); // 0=Sun
  const diff = (day === 0 ? -6 : 1 - day); // Mon as first day
  const mon = new Date(d);
  mon.setHours(0, 0, 0, 0);
  mon.setDate(d.getDate() + diff);
  return mon;
}

function toDateStr2(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const NAPTAR_PWA_LS = 'naptar_pwa_suggest_dismissed';

function NaptarPwaBanner() {
  const [visible, setVisible] = useState(() => !localStorage.getItem(NAPTAR_PWA_LS));
  if (!visible) return null;
  return (
    <div style={{
      background: '#14233F', color: '#fff', borderRadius: 10, padding: '12px 16px',
      marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
    }}>
      <span style={{ fontSize: 13, flex: 1, minWidth: 200 }}>
        Telepítsd a <strong>Naptár appot</strong> a naptar.modulum.hu-ról a gyors eléréshez.
      </span>
      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
        <a href="https://naptar.modulum.hu" target="_blank" rel="noreferrer"
          style={{ background: '#F47B20', color: '#fff', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 700, textDecoration: 'none' }}>
          Megnyitás
        </a>
        <button onClick={() => { localStorage.setItem(NAPTAR_PWA_LS, '1'); setVisible(false); }}
          style={{ background: 'rgba(255,255,255,.15)', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 12px', fontSize: 12, cursor: 'pointer' }}>
          Ne mutasd
        </button>
      </div>
    </div>
  );
}

function CalendarTab({ bizId }: { bizId: string }) {
  const [weekStart, setWeekStart] = useState(() => getWeekStart(new Date()));
  const [allBookings, setAllBookings] = useState<Booking[]>([]);
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [staffFilter, setStaffFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Booking | null>(null);

  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);

  useEffect(() => {
    get<Staff[]>(`/booking/${bizId}/staff`).then(r => setStaffList(r)).catch(() => {});
  }, [bizId]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ dateFrom: toDateStr2(weekStart), dateTo: toDateStr2(weekEnd), pageSize: '200' });
    if (staffFilter) params.set('staffId', staffFilter);
    get<{ bookings: Booking[]; total: number }>(`/booking/${bizId}/bookings?${params}`)
      .then(r => { setAllBookings(r.bookings || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [bizId, weekStart, staffFilter]);

  const bookings = allBookings;

  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    return d;
  });
  const DAYS_HU = ['H', 'K', 'Sze', 'Cs', 'P', 'Szo', 'V'];
  const HOURS = Array.from({ length: 14 }, (_, i) => i + 7); // 7:00–20:00

  function prevWeek() { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d); }
  function nextWeek() { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d); }
  function thisWeek() { setWeekStart(getWeekStart(new Date())); }

  function getBookingsForDay(day: Date) {
    const ds = toDateStr2(day);
    return bookings.filter(b => b.start_at.startsWith(ds) && b.status !== 'cancelled');
  }

  const today = toDateStr2(new Date());
  const weekLabel = `${weekStart.toLocaleDateString('hu-HU', { month: 'long', day: 'numeric' })} – ${weekEnd.toLocaleDateString('hu-HU', { month: 'long', day: 'numeric' })}`;

  return (
    <div>
      <NaptarPwaBanner />
      {/* Fejléc — egy sorban marad mobilon is */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, flexWrap: 'nowrap', overflowX: 'auto' }}>
        <button onClick={prevWeek} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'none', cursor: 'pointer', fontSize: 16, flexShrink: 0 }}>‹</button>
        <span style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', textAlign: 'center', flex: '0 0 auto', minWidth: 140 }}>{weekLabel}</span>
        <button onClick={nextWeek} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'none', cursor: 'pointer', fontSize: 16, flexShrink: 0 }}>›</button>
        <button onClick={thisWeek} style={{ padding: '4px 10px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'none', cursor: 'pointer', fontSize: 12, flexShrink: 0 }}>Ma</button>
        {staffList.length > 0 && (
          <select value={staffFilter} onChange={e => setStaffFilter(e.target.value)}
            style={{ fontSize: 12, padding: '4px 8px', borderRadius: 8, border: '1px solid var(--color-border)', flexShrink: 0 }}>
            <option value="">Mindenki</option>
            {staffList.map(s => <option key={s.id} value={s.id}>{s.display_name}</option>)}
          </select>
        )}
        {loading && <span style={{ fontSize: 12, color: 'var(--color-muted)', flexShrink: 0 }}>...</span>}
      </div>

      {/* Naptár grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '44px repeat(7, 1fr)', border: '1px solid var(--color-border)', borderRadius: 12, overflow: 'hidden', fontSize: 12 }}>
        {/* Fejléc sor */}
        <div style={{ background: 'var(--color-bg-subtle)', borderRight: '1px solid var(--color-border)', borderBottom: '1px solid var(--color-border)', padding: '6px 4px' }} />
        {days.map((d, i) => {
          const ds = toDateStr2(d);
          const isToday = ds === today;
          return (
            <div key={i} style={{ background: isToday ? 'rgba(99,102,241,.06)' : 'var(--color-bg-subtle)', borderLeft: '1px solid var(--color-border)', borderBottom: '1px solid var(--color-border)', padding: '6px 4px', textAlign: 'center' }}>
              <div style={{ fontWeight: 700, color: isToday ? 'var(--primary)' : undefined }}>{DAYS_HU[i]}</div>
              <div style={{ color: isToday ? 'var(--primary)' : 'var(--color-muted)', fontSize: 11 }}>{d.getDate()}</div>
            </div>
          );
        })}

        {/* Órasorok */}
        {HOURS.map(h => (
          <React.Fragment key={h}>
            <div style={{ borderRight: '1px solid var(--color-border)', borderTop: h > 7 ? '1px solid var(--color-border)' : undefined, padding: '2px 4px', textAlign: 'right', color: 'var(--color-muted)', background: 'var(--color-bg-subtle)', lineHeight: '40px', height: 40 }}>
              {h}:00
            </div>
            {days.map((d, di) => {
              const dayBookings = getBookingsForDay(d);
              const hourBookings = dayBookings.filter(b => {
                const bh = new Date(b.start_at).getHours();
                return bh === h;
              });
              const isToday = toDateStr2(d) === today;
              return (
                <div key={di} style={{ borderLeft: '1px solid var(--color-border)', borderTop: h > 7 ? '1px solid var(--color-border)' : undefined, height: 40, position: 'relative', background: isToday ? 'rgba(99,102,241,.02)' : undefined }}>
                  {hourBookings.map(b => (
                    <div key={b.id}
                      onClick={() => setSelected(b)}
                      title={`${b.guest_name} · ${fmtTime(b.start_at)}`}
                      style={{
                        position: 'absolute', left: 1, right: 1, top: `${(new Date(b.start_at).getMinutes() / 60) * 100}%`,
                        height: `${Math.max(18, Math.min(100, ((new Date(b.end_at).getTime() - new Date(b.start_at).getTime()) / 60000 / 60) * 100))}%`,
                        background: CAL_STATUS_COLOR[b.status] || '#6366f1',
                        borderRadius: 4, padding: '1px 3px', color: '#fff',
                        fontSize: 10, overflow: 'hidden', cursor: 'pointer', lineHeight: 1.3, zIndex: 1,
                      }}>
                      <div style={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.guest_name}</div>
                    </div>
                  ))}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>

      {/* Jelmagyarázat */}
      <div style={{ display: 'flex', gap: 12, marginTop: 12, fontSize: 12, flexWrap: 'wrap' }}>
        {Object.entries(CAL_STATUS_COLOR).map(([status, color]) => (
          <span key={status} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: color, display: 'inline-block' }} />
            {STATUS_LABEL[status] || status}
          </span>
        ))}
      </div>

      {/* Részlet panel */}
      {selected && (
        <div style={{ marginTop: 16, padding: 16, border: '1px solid var(--color-border)', borderRadius: 12, background: 'var(--color-bg-subtle)', position: 'relative' }}>
          <button onClick={() => setSelected(null)} style={{ position: 'absolute', top: 10, right: 12, background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--color-muted)' }}>×</button>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>{selected.guest_name}</div>
          <div style={{ fontSize: 13, color: 'var(--color-muted)', marginBottom: 2 }}>{fmtTime(selected.start_at)} – {fmtTime(selected.end_at)}</div>
          <div style={{ fontSize: 13 }}>{selected.guest_email}{selected.guest_phone ? ` · ${selected.guest_phone}` : ''}</div>
          {selected.staff_display_name && <div style={{ fontSize: 13, marginTop: 4 }}>Munkatárs: {selected.staff_display_name}</div>}
          {selected.services?.length ? <div style={{ fontSize: 13, marginTop: 4 }}>{selected.services.map(s => s.name).join(', ')}</div> : null}
          <div style={{ marginTop: 8 }}><Badge variant={STATUS_BADGE[selected.status] || 'default'}>{STATUS_LABEL[selected.status] || selected.status}</Badge></div>
        </div>
      )}
    </div>
  );
}

// ── Foglalóoldal stílusa tab ─────────────────────────────────────────────────

const PAGE_STYLES = [
  { id: 'classic',  label: 'Klasszikus',    color: '#c9a84c', bg: '#fffdf5', accent: '#c9a84c',  desc: 'Elegáns arany, letisztult' },
  { id: 'modern',   label: 'Modern',        color: '#6366f1', bg: '#f5f5ff', accent: '#6366f1',  desc: 'Merész lila, geometrikus' },
  { id: 'dark',     label: 'Sötét',         color: '#14233F', bg: '#111827', accent: '#F47B20',  desc: 'Mélykék, narancs akcentus' },
  { id: 'nature',   label: 'Természet',     color: '#2d6a4f', bg: '#f0fdf4', accent: '#52b788',  desc: 'Zöld, természetes érzés' },
  { id: 'luxury',   label: 'Prémium',       color: '#1a1a2e', bg: '#fdf6e3', accent: '#d4af37',  desc: 'Sötét, arany részletek' },
  { id: 'playful',  label: 'Vidám',         color: '#e11d48', bg: '#fff1f2', accent: '#f472b6',  desc: 'Élénk rózsaszín, szórakoztató' },
];

function StylePreview({ s, selected, onClick }: { s: typeof PAGE_STYLES[0]; selected: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} style={{
      border: selected ? `2.5px solid ${s.color}` : '2px solid var(--color-border)',
      borderRadius: 14, padding: 0, cursor: 'pointer', background: 'none',
      overflow: 'hidden', width: '100%', transition: 'border-color .15s',
    }}>
      {/* Mock booking page */}
      <div style={{ background: s.bg, padding: '12px 10px 8px' }}>
        <div style={{ background: s.color, borderRadius: 8, height: 36, marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: 60, height: 8, borderRadius: 4, background: 'rgba(255,255,255,.6)' }} />
        </div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
          {[1,2,3].map(i => (
            <div key={i} style={{ flex: 1, background: i === 1 ? s.accent + '30' : '#e5e7eb', borderRadius: 6, height: 28, border: i === 1 ? `1.5px solid ${s.accent}` : 'none' }} />
          ))}
        </div>
        <div style={{ background: s.accent, borderRadius: 6, height: 24, marginTop: 4 }} />
      </div>
      <div style={{ padding: '8px 10px', background: '#fff', borderTop: '1px solid var(--color-border)', textAlign: 'left' }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: s.color }}>{s.label}</div>
        <div style={{ fontSize: 11, color: 'var(--color-muted)' }}>{s.desc}</div>
      </div>
    </button>
  );
}

function PageStyleTab({ bizId }: { bizId: string }) {
  const { toast } = useToast();
  const [config, setConfig] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);

  useEffect(() => {
    get<any>(`/booking/${bizId}/page-config`)
      .then(c => { setConfig(c || {}); setLoading(false); })
      .catch(() => setLoading(false));
  }, [bizId]);

  async function selectStyle(styleId: string) {
    const styleObj = PAGE_STYLES.find(s => s.id === styleId);
    if (!styleObj) return;
    setSaving(true);
    try {
      const updated = await patch<any>(`/booking/${bizId}/page-config`, {
        style: styleId,
        primary_color: styleObj.color,
      });
      setConfig(updated);
      toast('Stílus mentve.', 'success');
    } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
    setSaving(false);
  }

  async function uploadHeaderImage(file: File) {
    setImageUploading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const fd = new FormData();
      fd.append('photo', file);
      const res = await fetch(`/api/booking/${bizId}/page-header-image`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Hiba.'); }
      const { header_image_url } = await res.json();
      const updated = await patch<any>(`/booking/${bizId}/page-config`, { header_image_url });
      setConfig(updated);
      toast('Fejléc kép feltöltve.', 'success');
    } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
    setImageUploading(false);
  }

  async function removeHeaderImage() {
    const updated = await patch<any>(`/booking/${bizId}/page-config`, { header_image_url: null });
    setConfig(updated);
    toast('Fejléc kép törölve.', 'success');
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Betöltés...</div>;

  const currentStyle = config?.style || 'classic';

  return (
    <div style={{ maxWidth: 720 }}>
      <LoadingOverlay visible={saving || imageUploading} />
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Foglalóoldal stílusa</div>
        <div style={{ fontSize: 13, color: 'var(--color-muted)' }}>
          Válaszd ki az online foglalási oldalad megjelenítési stílusát.
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 28 }}>
        {PAGE_STYLES.map(s => (
          <StylePreview key={s.id} s={s} selected={currentStyle === s.id} onClick={() => selectStyle(s.id)} />
        ))}
      </div>

      <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 20 }}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Fejléc háttérkép</div>
        <div style={{ fontSize: 13, color: 'var(--color-muted)', marginBottom: 12 }}>
          Opcionálisan tölts fel egy képet a foglalóoldal fejlécébe. Max. 5 MB, JPG/PNG.
        </div>
        {config?.header_image_url && (
          <div style={{ marginBottom: 12 }}>
            <img src={config.header_image_url} alt="Header" style={{ width: '100%', maxWidth: 400, height: 120, objectFit: 'cover', borderRadius: 10, border: '1px solid var(--color-border)' }} />
            <div style={{ marginTop: 6 }}>
              <Button size="sm" variant="ghost" onClick={removeHeaderImage}>Kép eltávolítása</Button>
            </div>
          </div>
        )}
        <label style={{ display: 'inline-flex', cursor: 'pointer' }}>
          <input type="file" accept="image/*" style={{ display: 'none' }}
            onChange={e => { const f = e.target.files?.[0]; if (f) uploadHeaderImage(f); e.target.value = ''; }} />
          <span style={{ fontSize: 13, padding: '7px 14px', borderRadius: 8, border: '1px solid var(--color-border)', cursor: 'pointer', background: 'transparent' }}>
            {config?.header_image_url ? '🔄 Kép cserélése' : '📤 Kép feltöltése'}
          </span>
        </label>
      </div>
    </div>
  );
}

// ── Főkomponens ───────────────────────────────────────────────────────────────

export default function BookingAdminPage() {
  const { toast } = useToast();
  const location = useLocation();
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [biz, setBiz] = useState<Business | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newForm, setNewForm] = useState({ name: '', slug: '', category: 'other', subdomain_provider: 'modulum' as 'modulum' | 'foglalopont' });
  const [creating, setCreating] = useState(false);
  const [staff, setStaff] = useState<Staff[]>([]);

  useEffect(() => {
    get<Business[]>('/booking').then(r => {
      setBusinesses(r);
      if (r.length > 0) setBiz(r[0]);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  function closeCreate() {
    setShowCreate(false);
    setNewForm({ name: '', slug: '', category: 'other', subdomain_provider: 'modulum' });
  }

  async function createBusiness() {
    if (!newForm.name) return;
    setCreating(true);
    try {
      const created = await post<Business>('/booking', {
        name: newForm.name,
        slug: newForm.slug || newForm.name.toLowerCase()
          .replace(/[áàä]/g, 'a').replace(/[éè]/g, 'e').replace(/[íì]/g, 'i')
          .replace(/[óöő]/g, 'o').replace(/[úüű]/g, 'u')
          .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''),
        category: newForm.category,
        subdomain_provider: newForm.subdomain_provider,
      });
      setBusinesses(bs => [...bs, created]);
      setBiz(created);
      closeCreate();
      toast('Foglalási profil létrehozva!', 'success');
    } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
    setCreating(false);
  }

  if (loading) return <div style={{ padding: 60, textAlign: 'center' }}>Betöltés...</div>;

  return (
    <div style={{ padding: '24px 32px', maxWidth: 900 }}>
      <PageHeader title="Időpontfoglaló" subtitle="Foglalási profil és naptár kezelése" />

      {/* Business picker (ha több van) */}
      {businesses.length > 1 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          {businesses.map(b => (
            <button key={b.id}
              onClick={() => setBiz(b)}
              style={{ padding: '6px 16px', borderRadius: 20, border: '1px solid var(--color-border)', fontSize: 13, fontWeight: biz?.id === b.id ? 700 : 400, background: biz?.id === b.id ? 'var(--primary)' : 'white', color: biz?.id === b.id ? 'white' : 'inherit', cursor: 'pointer' }}>
              {b.name}
            </button>
          ))}
          <Button size="sm" variant="ghost" onClick={() => setShowCreate(true)}>+ Új profil</Button>
        </div>
      )}

      {/* Nincs még profil */}
      {!biz && (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📅</div>
          <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Nincs még foglalási profil</h2>
          <p style={{ color: 'var(--color-muted)', marginBottom: 24, fontSize: 14 }}>
            Hozz létre egy profilt, és máris elkezdheted fogadni az online időpontfoglalásokat.
          </p>
          <Button onClick={() => setShowCreate(true)}>Foglalási profil létrehozása</Button>
        </div>
      )}

      {/* Profil tabs */}
      {biz && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <span style={{ fontWeight: 700, fontSize: 16 }}>{biz.name}</span>
            <Badge variant={biz.status === 'active' ? 'success' : 'warning'}>{biz.status === 'active' ? 'Aktív' : biz.status}</Badge>
          </div>
          <Tabs
            key={location.search}
            defaultIndex={new URLSearchParams(location.search).get('tab') === 'naptar' ? 6 : 0}
            tabs={[
              { label: 'Alapadatok', content: <BasicInfoTab biz={biz} onSaved={b => { setBiz(b); setBusinesses(bs => bs.map(x => x.id === b.id ? b : x)); }} /> },
              { label: 'Beállítások', content: <SettingsTab bizId={biz.id} /> },
              { label: 'Szolgáltatások', content: <ServicesTab bizId={biz.id} staff={staff} /> },
              { label: 'Munkatársak', content: <StaffTab bizId={biz.id} onStaffChange={setStaff} /> },
              { label: 'Nyitvatartás', content: <HoursTab bizId={biz.id} /> },
              { label: 'Foglalások', content: <BookingsTab bizId={biz.id} /> },
              { label: '🎨 Foglalóoldal', content: <PageStyleTab bizId={biz.id} /> },
              { label: '🗓️ Naptár', content: <CalendarTab bizId={biz.id} /> },
            ]}
          />
        </>
      )}

      {/* Új profil modal */}
      <Modal isOpen={showCreate} onClose={closeCreate} title="Új foglalási profil"
        footer={<><Button onClick={createBusiness} disabled={creating || !newForm.name}>Létrehozás</Button><Button variant="ghost" onClick={closeCreate}>Mégsem</Button></>}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Input label="Üzlet neve *" value={newForm.name}
            onChange={e => setNewForm(f => ({ ...f, name: e.target.value }))} />
          <Input label="Slug (opcionális)" value={newForm.slug} placeholder="auto-generált"
            helperText={`pl. barber-xyz → barber-xyz.${newForm.subdomain_provider}.hu`}
            onChange={e => setNewForm(f => ({ ...f, slug: e.target.value }))} />
          <Select label="Domain" value={newForm.subdomain_provider}
            options={[
              { value: 'modulum', label: 'slug.modulum.hu' },
              { value: 'foglalopont', label: 'slug.foglalopont.hu' },
            ]}
            onChange={e => setNewForm(f => ({ ...f, subdomain_provider: e.target.value as 'modulum' | 'foglalopont' }))} />
          <Select label="Kategória" value={newForm.category} options={CATEGORY_OPTIONS}
            onChange={e => setNewForm(f => ({ ...f, category: e.target.value }))} />
        </div>
      </Modal>
    </div>
  );
}
// build 1789668466
